import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import moment from 'moment';
import styled from 'styled-components';

import {
  TCalendarDay,
  TCalendarRow,
  TONE,
  ToneDot,
  fmtMoney,
  toneColor,
} from '../shared';
import { buildAgenda, IAgendaDayEntry } from './agendaModel';
import { AgendaRow } from './AgendaRow';

export interface IAgendaFeedProps {
  /** Every fetched day of the active horizon (ascending or not — re-sorted). */
  days: TCalendarDay[];
  /** Open a row in its edit surface. */
  onEdit: (row: TCalendarRow) => void;
  /** Delete a deletable row. */
  onDelete: (row: TCalendarRow) => void;
  /** Called after a successful reschedule (refetch). */
  onMoved?: () => void;
  /** Mark an unpaid expense row as paid. */
  onMarkPaid: (row: TCalendarRow) => void;
  /** Set an expense row to any status. */
  onSetStatus: (row: TCalendarRow, status: string) => void;
}

/** Rows shown per column before collapsing the rest behind «показать ещё N». */
const MAX_VISIBLE = 8;

const rowKey = (row: TCalendarRow): string =>
  row.sourceRef || `${row.title}-${row.date}-${row.amountPln}`;

/** «5–7 июля» / «5 июля – 2 августа» / «5 июля» for a quiet-day run. */
const gapLabel = (fromIso: string, toIso: string): string => {
  const from = moment(fromIso);
  const to = moment(toIso);
  if (fromIso === toIso) return from.format('D MMMM');
  if (from.isSame(to, 'month')) {
    return `${from.format('D')}–${to.format('D MMMM')}`;
  }
  return `${from.format('D MMMM')} – ${to.format('D MMMM')}`;
};

/**
 * The «Обзор» agenda: one card per day with movements — a 2-column body
 * («Придёт» left / «Уйдёт» right), a tone dot in the header and the running
 * end-of-day balance in the footer. Runs of quiet days collapse into a single
 * muted «нет движений» line. Column overflow is capped per day and expanded
 * in place.
 */
export const AgendaFeed = memo<IAgendaFeedProps>(
  ({ days, onEdit, onDelete, onMoved, onMarkPaid, onSetStatus }) => {
    const { t } = useTranslation();
    const entries = useMemo(() => buildAgenda(days), [days]);

    // Which day-columns are expanded past MAX_VISIBLE ('date:in' / 'date:out').
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const expand = useCallback((key: string) => {
      setExpanded((prev) => ({ ...prev, [key]: true }));
    }, []);

    const renderColumn = (
      entry: IAgendaDayEntry,
      side: 'in' | 'out',
      rows: TCalendarRow[],
    ) => {
      const key = `${entry.date}:${side}`;
      const isOpen = !!expanded[key];
      const visible = isOpen ? rows : rows.slice(0, MAX_VISIBLE);
      const hiddenCount = rows.length - visible.length;
      return (
        <Column side={side}>
          <div className="af-col-head">
            {side === 'in'
              ? t('pages.paymentCalendar.agenda.incoming')
              : t('pages.paymentCalendar.agenda.outgoing')}
          </div>
          {rows.length === 0 ? (
            <div className="af-col-empty">—</div>
          ) : (
            <>
              {visible.map((row) => (
                <AgendaRow
                  key={rowKey(row)}
                  row={row}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onMoved={onMoved}
                  onMarkPaid={onMarkPaid}
                  onSetStatus={onSetStatus}
                />
              ))}
              {hiddenCount > 0 && (
                <Button size="small" type="link" onClick={() => expand(key)}>
                  {t('pages.paymentCalendar.agenda.showMore', {
                    n: hiddenCount,
                  })}
                </Button>
              )}
            </>
          )}
        </Column>
      );
    };

    if (entries.length === 0) {
      return (
        <QuietRow>{t('pages.paymentCalendar.agenda.noMovements')}</QuietRow>
      );
    }

    return (
      <Feed>
        {entries.map((entry) => {
          if (entry.type === 'gap') {
            return (
              <QuietRow key={`gap-${entry.from}`}>
                {gapLabel(entry.from, entry.to)} —{' '}
                {t('pages.paymentCalendar.agenda.noMovements')}
              </QuietRow>
            );
          }
          const end = entry.day.endBalancePln || 0;
          return (
            <DayCard key={entry.date}>
              <DayHead>
                <span className="af-date">
                  {moment(entry.date).format('dd, DD.MM')}
                </span>
                <ToneDot tone={entry.day.tone ?? end} />
              </DayHead>
              <DayBody>
                {renderColumn(entry, 'in', entry.incoming)}
                {renderColumn(entry, 'out', entry.outgoing)}
              </DayBody>
              <DayFoot toned={toneColor(entry.day.tone ?? end)}>
                {t('pages.paymentCalendar.agenda.endBalance')}:{' '}
                <strong>{fmtMoney(end)}</strong>
              </DayFoot>
            </DayCard>
          );
        })}
      </Feed>
    );
  },
);

/* ----------------------------- styles ----------------------------- */

const Feed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DayCard = styled.div`
  border: 1px solid #ededed;
  border-radius: 12px;
  background: #fff;
  padding: 12px 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`;

const DayHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  .af-date {
    font-size: 14px;
    font-weight: 700;
    text-transform: capitalize;
  }
`;

const DayBody = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Column = styled.div<{ side: 'in' | 'out' }>`
  min-width: 0;
  border-left: 3px solid
    ${({ side }) => (side === 'in' ? TONE.ok : TONE.danger)};
  padding-left: 10px;

  .af-col-head {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: ${({ side }) => (side === 'in' ? TONE.ok : TONE.danger)};
    margin-bottom: 4px;
  }
  .af-col-empty {
    color: #cbd5e1;
    font-size: 12px;
  }
`;

const DayFoot = styled.div<{ toned: string }>`
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
  font-size: 12px;
  color: #64748b;

  strong {
    color: ${({ toned }) => toned};
    font-variant-numeric: tabular-nums;
  }
`;

const QuietRow = styled.div`
  padding: 8px 16px;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
  color: #94a3b8;
  font-size: 12px;
  background: #fafafa;
`;
